import { formatINR } from '@neyara/shared';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { useCart } from '../lib/cart';
import { lockScroll } from '../lib/scroll';
import { useWhatsApp } from '../lib/whatsapp';
import { CloseIcon, MinusIcon, PlusIcon, TrashIcon, WhatsAppIcon } from './icons';
import { Lotus, LotusDivider } from './lotus';

/** The bag: a maroon panel like the board's shopping bag, ending in one WhatsApp order. */
export function CartDrawer() {
  const { items, total, count, isOpen, close, setQuantity, remove } = useCart();
  const whatsapp = useWhatsApp();
  const closeButton = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    returnFocus.current = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();
    lockScroll(true);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key !== 'Tab' || !panel.current) return;
      const focusables = panel.current.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input');
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      lockScroll(false);
      window.removeEventListener('keydown', onKey);
      returnFocus.current?.focus();
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  const orderHref = whatsapp?.order(
    items.map((i) => ({ name: i.name, slug: i.slug, price: i.price, quantity: i.quantity })),
  );

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 animate-[fade-in_.3s_ease-out] bg-charcoal/45 backdrop-blur-[2px]"
        onClick={close}
        aria-hidden="true"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bag-title"
        className="on-maroon absolute inset-y-0 right-0 flex w-full max-w-md animate-[drawer-in_.45s_var(--ease-out-expo)] flex-col bg-maroon text-champagne shadow-[-20px_0_40px_-20px_rgb(0_0_0/.45)]"
      >
        <div className="flex items-center justify-between border-b border-gold/30 px-6 py-5">
          <h2 id="bag-title" className="font-display text-lg tracking-[0.24em] uppercase">
            Your bag{count > 0 && <span className="tabular ml-2 text-gold">({count})</span>}
          </h2>
          <button
            ref={closeButton}
            type="button"
            onClick={close}
            className="-mr-2 grid size-11 place-items-center transition-colors hover:text-gold"
            aria-label="Close bag"
          >
            <CloseIcon />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
            <Lotus className="h-10 w-16 text-gold" />
            <p className="mt-5 font-script text-3xl italic">Your bag is empty</p>
            <p className="mt-2 text-champagne/75">Sarees you add will wait here until you order.</p>
            <Link to="/sarees" onClick={close} className="btn btn-gold mt-8">
              Browse the sarees
            </Link>
          </div>
        ) : (
          <>
            {/* Light paper inside the maroon bag so the items read clearly. */}
            <ul className="flex-1 divide-y divide-champagne-deep overflow-y-auto bg-tissue px-6 text-charcoal [&_:focus-visible]:outline-maroon">
              {items.map((item) => (
                <li key={item.id} className="flex gap-4 py-5">
                  <Link
                    to={`/sarees/${item.slug}`}
                    onClick={close}
                    className="block h-24 w-[4.5rem] shrink-0 overflow-hidden bg-champagne ring-1 ring-champagne-deep"
                  >
                    {item.image ? (
                      <img src={item.image} alt="" className="size-full object-cover" />
                    ) : (
                      <span className="grid size-full place-items-center text-gold-deep">
                        <Lotus className="h-5 w-8" />
                      </span>
                    )}
                  </Link>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <Link
                      to={`/sarees/${item.slug}`}
                      onClick={close}
                      className="line-clamp-2 font-display text-[0.8rem] leading-snug tracking-[0.1em] text-maroon uppercase hover:text-maroon-soft"
                    >
                      {item.name}
                    </Link>
                    <p className="tabular mt-1 text-ink-soft">{formatINR(item.price)}</p>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center border border-maroon/30 bg-white/60">
                        <button
                          type="button"
                          className="grid size-9 cursor-pointer place-items-center text-maroon hover:bg-champagne/60 disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent"
                          onClick={() => setQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          aria-label={`One fewer ${item.name}`}
                        >
                          <MinusIcon size={16} />
                        </button>
                        <span className="tabular w-8 text-center text-sm" aria-live="polite">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          className="grid size-9 cursor-pointer place-items-center text-maroon hover:bg-champagne/60 disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent"
                          onClick={() => setQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= item.stock}
                          aria-label={`One more ${item.name}`}
                        >
                          <PlusIcon size={16} />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(item.id)}
                        className="-mr-2 grid size-9 cursor-pointer place-items-center text-ink-soft transition-colors hover:text-maroon"
                        aria-label={`Remove ${item.name}`}
                      >
                        <TrashIcon size={18} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-t border-gold/30 px-6 pt-5 pb-6">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-sm tracking-[0.22em] uppercase">Total</span>
                <span className="tabular text-xl font-medium">{formatINR(total)}</span>
              </div>
              {orderHref ? (
                <a href={orderHref} target="_blank" rel="noopener noreferrer" className="btn btn-gold mt-5 w-full">
                  <WhatsAppIcon size={20} /> Order on WhatsApp
                </a>
              ) : (
                <button type="button" disabled className="btn btn-gold mt-5 w-full">
                  Ordering opens soon
                </button>
              )}
              <LotusDivider className="mt-5 opacity-70" />
              <p className="mt-3 text-center text-sm text-champagne/75">
                WhatsApp opens with your order written out. We confirm availability, delivery and payment in the chat.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
